import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

internal data class ScoreProgression(
    val id: String,
    val label: String,
    val color: String,
    val values: List<Double?>,
    val samples: List<Int>,
    val dashed: Boolean = false,
)

internal enum class ScoreChartMode(val label: String, val title: String) {
    CUMULATIVE("Průběžné skóre", "Průběžné skóre po framech"),
    DIFFERENCE("Odchylka od sezóny", "Odchylka od sezónního průměru"),
    FRAME_GAIN("Zisk za frame", "Bodový zisk za frame"),
}

internal fun scoreChartValues(values: List<Double?>, mode: ScoreChartMode, baseline: List<Double?> = emptyList()): List<Double?> =
    values.mapIndexed { index, value ->
        when (mode) {
            ScoreChartMode.CUMULATIVE -> value
            ScoreChartMode.DIFFERENCE -> baseline.getOrNull(index)?.let { average -> value?.minus(average) }
            ScoreChartMode.FRAME_GAIN -> {
                val previous = if (index == 0) 0.0 else values[index - 1]
                if (value != null && previous != null) value - previous else null
            }
        }
    }

internal fun cumulativeScores(game: DetailedGame): List<Double?> =
    Scoring.resolvedFrameCumulative(game.game.gameType, game.sheet.frames.map { it.t }).map { it?.toDouble() }

internal fun seasonScoreProgression(details: List<DetailedGame>, mode: ScoreChartMode = ScoreChartMode.CUMULATIVE): ScoreProgression {
    // Average per-game gains, not differences of averages with potentially different samples.
    val scores = details.map { scoreChartValues(cumulativeScores(it), if (mode == ScoreChartMode.DIFFERENCE) ScoreChartMode.CUMULATIVE else mode) }
    val byFrame = (0 until 10).map { frame -> scores.mapNotNull { it[frame] } }
    return ScoreProgression("season", "Sezónní průměr", TEAM_AVG_COLOR,
        byFrame.map { values -> values.takeIf { it.isNotEmpty() }?.let { if (mode == ScoreChartMode.DIFFERENCE) 0.0 else it.average() } }, byFrame.map { it.size }, dashed = true)
}

@Composable
fun CumulativeScoreChart(displayed: List<DetailedGame>, season: List<DetailedGame>, names: Map<String, String>) {
    var mode by remember { mutableStateOf(ScoreChartMode.CUMULATIVE) }
    H4 { Text(mode.title) }
    StatsTabs(mode.name, ScoreChartMode.entries.map { it.name to it.label }) { selected -> mode = ScoreChartMode.valueOf(selected) }
    val baseline = seasonScoreProgression(season)
    val games = displayed.sortedWith(compareBy<DetailedGame> { names[it.game.playerId] ?: it.game.playerId }.thenBy { it.game.gameNo })
    val series = games.mapIndexed { index, d ->
        ScoreProgression("${d.game.playerId}:${d.game.sheetKey()}", "${names[d.game.playerId] ?: d.game.playerId} · ${d.game.gameNo}. hra",
            playerColor(index), scoreChartValues(cumulativeScores(d), mode, baseline.values), List(10) { 1 })
    }.filter { it.values.any { score -> score != null } } + listOf(seasonScoreProgression(season, mode))
        .filter { it.values.any { score -> score != null } }
    if (displayed.isEmpty() || series.none { !it.dashed }) {
        P(attrs = { classes("muted") }) { Text("Pro graf zatím není dostupný zápis s uzavřeným skóre alespoň jednoho framu.") }
        return
    }
    var hidden by remember { mutableStateOf<Set<String>>(emptySet()) }
    Div(attrs = { classes("time-chart-legend", "score-progress-legend") }) {
        series.forEach { s ->
            Label(attrs = { classes("cms-check") }) {
                CheckboxInput(checked = s.id !in hidden) { onChange { hidden = if (it.value) hidden - s.id else hidden + s.id } }
                Span(attrs = {
                    classes("score-progress-swatch")
                    attr("style", "border-top:2px ${if (s.dashed) "dashed" else "solid"} ${s.color}")
                }) {}
                Text(" ${s.label}")
            }
        }
    }
    val visible = series.filter { it.id !in hidden }
    if (visible.isEmpty()) {
        P(attrs = { classes("muted") }) { Text("Vyber alespoň jednu křivku.") }
    } else {
        val svg = buildScoreProgressionSvg(visible, mode)
        key(svg) {
            Div(attrs = { classes("time-chart", "score-progress-chart"); ref { it.innerHTML = svg; onDispose {} } }) {}
        }
    }
    P(attrs = { classes("muted") }) {
        Text(when (mode) {
            ScoreChartMode.CUMULATIVE -> "Plné čáry: průběžné součty jednotlivých her. Čárkovaně: sezónní průměr po každém framu. "
            ScoreChartMode.DIFFERENCE -> "Čáry ukazují rozdíl průběžného skóre proti sezónnímu průměru ve stejném framu. Kladné hodnoty jsou nad průměrem, záporné pod ním; nula odpovídá sezónnímu průměru. "
            ScoreChartMode.FRAME_GAIN -> "Sloupce: bodový zisk jednotlivých framů včetně bonusů. Čárkovaná čára: průměrný zisk za stejný frame v sezóně. "
        })
        Text("Sezónní vzorek: ${season.size} zápisů podle filtrů sezóny, zdrojů, typu a případně hráče. Neuzavřené framy čekají na bonusové hody a v grafu se nezobrazují. Sezónní reference zůstává při výběru události nebo hry stejná.")
    }
}

private fun chartXml(value: String): String = value.replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;").replace("\"", "&quot;").replace("'", "&apos;")

internal fun buildScoreProgressionSvg(series: List<ScoreProgression>, mode: ScoreChartMode = ScoreChartMode.CUMULATIVE): String {
    val w = 640; val h = 280; val left = 44; val right = 622; val top = 20; val bottom = 232
    val values = series.flatMap { it.values }.filterNotNull()
    val max = values.maxOrNull() ?: 0.0
    val min = values.minOrNull() ?: 0.0
    val stepSize = when (mode) { ScoreChartMode.CUMULATIVE -> 50; ScoreChartMode.DIFFERENCE -> 10; ScoreChartMode.FRAME_GAIN -> 5 }
    val ymin = minOf(0.0, kotlin.math.floor(min / stepSize) * stepSize)
    val ymax = (kotlin.math.ceil(max / stepSize) * stepSize).coerceAtLeast(stepSize.toDouble())
    val band = (right - left) / 10.0
    fun x(frame: Int) = if (mode == ScoreChartMode.FRAME_GAIN) left + band * (frame - 0.5) else left + band * frame
    fun y(score: Double) = bottom - (bottom - top) * (score - ymin) / (ymax - ymin)
    val grid = ((ymin / stepSize).toInt()..(ymax / stepSize).toInt()).joinToString("") { step ->
        val value = step * stepSize
        "<line x1='$left' y1='${y(value.toDouble())}' x2='$right' y2='${y(value.toDouble())}' stroke='#2c3240'/><text x='${left - 7}' y='${y(value.toDouble()) + 4}' fill='#9aa0aa' font-size='11' text-anchor='end'>$value</text>"
    }
    val labels = ((if (mode == ScoreChartMode.FRAME_GAIN) 1 else 0)..10).joinToString("") { frame ->
        "<text x='${x(frame)}' y='${bottom + 18}' fill='#9aa0aa' font-size='11' text-anchor='middle'>$frame</text>"
    }
    val barSeries = series.filter { !it.dashed }
    val curves = series.joinToString("") { s ->
        fun tooltip(index: Int, value: Double): String {
            val sample = if (s.dashed) " · ${s.samples[index]} her" else ""
            return "${chartXml(s.label)} · ${index + 1}. frame: ${round1(value)} bodů$sample"
        }
        if (mode == ScoreChartMode.FRAME_GAIN && !s.dashed) {
            val barWidth = band * 0.8 / barSeries.size.coerceAtLeast(1)
            val position = barSeries.indexOf(s)
            s.values.mapIndexedNotNull { index, value -> value?.let {
                val bx = x(index + 1) - band * 0.4 + position * barWidth
                "<rect x='$bx' y='${minOf(y(it), y(0.0))}' width='${barWidth * 0.9}' height='${kotlin.math.abs(y(it) - y(0.0)).coerceAtLeast(0.5)}' fill='${s.color}'><title>${tooltip(index, it)}</title></rect>"
            } }.joinToString("")
        } else {
            val points = buildList {
                if (mode != ScoreChartMode.FRAME_GAIN) add("${x(0)},${y(0.0)}")
                s.values.forEachIndexed { index, value -> if (value != null) add("${x(index + 1)},${y(value)}") }
            }.joinToString(" ")
            val dash = if (s.dashed) " stroke-dasharray='7 5'" else ""
            val dots = s.values.mapIndexedNotNull { index, value -> value?.let {
                "<circle cx='${x(index + 1)}' cy='${y(it)}' r='3' fill='${s.color}'><title>${tooltip(index, it)}</title></circle>"
            } }.joinToString("")
            "<polyline points='$points' fill='none' stroke='${s.color}' stroke-width='2'$dash/>$dots"
        }
    }
    val zero = if (mode == ScoreChartMode.DIFFERENCE) "<line x1='$left' y1='${y(0.0)}' x2='$right' y2='${y(0.0)}' stroke='#9aa0aa' stroke-dasharray='3 3'/>" else ""
    return "<svg viewBox='0 0 $w $h' width='100%' role='img' aria-label='${mode.title}' xmlns='http://www.w3.org/2000/svg'><title>${mode.title}</title>$grid$labels$zero$curves<text x='$left' y='12' fill='#9aa0aa' font-size='11'>Body</text><text x='${(left + right) / 2}' y='273' fill='#9aa0aa' font-size='11' text-anchor='middle'>Frame</text></svg>"
}
